import { useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Container } from "@/components/site/Container";
import { PageHero } from "@/components/site/PageHero";
import { ButtonLink } from "@/components/site/Button";
import { businessNode, canonical } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { loadPageSeo } from "@/lib/page-seo-loader";
import { applySeoOverrides } from "@/lib/seo-overrides";
import { submitLead } from "@/lib/leads.functions";
import { trackConversion } from "@/lib/openai-ads";
import { useLeadGuard } from "@/components/site/lead-honeypot";
import { getAttribution } from "@/lib/meta-attribution";
import { Phone, MessageSquare, Mail, MapPin, BadgeCheck } from "lucide-react";

const TITLE = "Contact Durafoam Roofing | Phoenix, AZ | 480-941-5373";
const DESCRIPTION =
  "Contact Durafoam Roofing in Phoenix, AZ — 2810 E Mohawk Ln. Call 480-941-5373 or send a message for a free roof inspection. We respond right away.";

const localBusinessSchema = businessNode({ url: "/contact-us" });


export const Route = createFileRoute("/contact-us")({
  loader: () => loadPageSeo("contact-us"),
  head: ({ loaderData }) => {
    const url = canonical("/contact-us");
    return applySeoOverrides({
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        ...ogImageMeta("contact-us"),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(localBusinessSchema),
        },
      ],
    }, loaderData);
  },
  component: ContactPage,
});

const services = [
  "Free inspection",
  "Roof leak repair",
  "Foam roof recoating",
  "Foam roof replacement",
  "Tile roof R&R",
  "Shingle roof",
  "Flat roof repair",
  "Skylight repair",
  "Commercial / HOA",
  "Other",
];

function ContactPage() {
  const submit = useServerFn(submitLead);
  const navigate = useNavigate();
  const { honeypot, guardFields } = useLeadGuard();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: (input: { name: string; phone: string; email: string; service: string; message: string }) =>
      submit({ data: { ...input, source: "contact-us", attribution: getAttribution({ funnel: "contact", lead_type: "contact_inquiry", form_name: "contact_form" }), source_page: typeof window !== "undefined" ? window.location.pathname : "/contact-us", ...guardFields() } }),
    onSuccess: (res) => {
      if (res?.stored && res.lead_id) {
        trackConversion("form_submit", { dedupeKey: `form:${res.lead_id}`, leadId: res.lead_id });
      }
      setName("");
      setPhone("");
      setEmail("");
      setService("");
      setMessage("");
      navigate({ to: "/thank-you" });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Something went wrong. Please call us.");
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    mutation.mutate({ name, phone, email, service, message });
  }

  return (
    <>
      {/* Hero */}
      <PageHero
        pageSlug="contact-us"
        compact
        eyebrow="Contact us"
        h1="Let's talk about your roof."
        subhead="Call or send a message — we respond right away. For urgent leaks, phone is fastest."
      >
        <p className="mt-5 text-lg font-semibold text-white">
          Have a roofing question? Text us now.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:hidden">
          <ButtonLink href="sms:+14809415373" variant="primary" size="md" className="w-full">
            Text us
          </ButtonLink>
          <ButtonLink href="tel:+14809415373" variant="ghost-light" size="md" className="w-full">
            Call 480-941-5373
          </ButtonLink>
        </div>
        <div className="mt-4 hidden sm:block">
          <ButtonLink href="tel:+14809415373" variant="primary" size="md">
            Call or text us 480-941-5373
          </ButtonLink>
        </div>
      </PageHero>


      {/* Trust / proof */}
      <section className="border-b border-border bg-background py-8 lg:py-10">
        <Container>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <p className="text-lg font-semibold text-primary">
              30,000+ roofs serviced across the Phoenix Valley since 1989
            </p>
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm">
              <BadgeCheck className="h-5 w-5 text-accent" aria-hidden="true" />
              BBB Accredited Since 2008
            </div>
          </div>
        </Container>
      </section>

      {/* Two-column */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr]">
            {/* Form */}
            <div className="rounded-2xl border border-border bg-white p-6 sm:p-8 shadow-[0_10px_30px_-12px_rgba(15,26,66,0.12)]">
              <h2 className="text-2xl font-bold text-primary">Send us a message</h2>
              <p className="mt-2 text-sm text-[var(--muted-foreground)]">
                We respond right away. Phone is fastest for urgent leaks.
              </p>
              <form onSubmit={onSubmit} className="relative mt-6 space-y-4" noValidate>
                {honeypot}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field id="c-name" label="Your name *">
                    <input
                      id="c-name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                      required
                      className={inputCls}
                    />
                  </Field>
                  <Field id="c-phone" label="Phone *">
                    <input
                      id="c-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      maxLength={20}
                      required
                      className={inputCls}
                    />
                  </Field>
                </div>
                <Field id="c-email" label="Email (optional)">
                  <input
                    id="c-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={120}
                    className={inputCls}
                  />
                </Field>
                <Field id="c-service" label="What do you need?">
                  <select
                    id="c-service"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select a service…</option>
                    {services.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="c-message" label="Message">
                  <textarea
                    id="c-message"
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={2000}
                    placeholder="Tell us a little about your roof or the issue — for photos, text us instead."
                    className={inputCls}
                  />
                </Field>
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60 sm:w-auto"
                >
                  {mutation.isPending ? "Sending…" : "Send message"}
                </button>
                <p className="text-xs text-[var(--muted-foreground)]">
                  By submitting, you agree we may call the number provided. We don't share
                  your info.
                </p>
              </form>
            </div>

            {/* Info */}
            <div className="space-y-5">
              <InfoCard icon={Phone} title="Call us">
                <a href="tel:+14809415373" className="text-lg font-semibold text-primary">
                  480-941-5373
                </a>
              </InfoCard>
              <InfoCard icon={MessageSquare} title="Text us">
                <a href="sms:+14809415373" className="text-lg font-semibold text-primary">
                  480-941-5373
                </a>
                <p className="text-sm text-[var(--muted-foreground)]">Send photos of the issue — we'll reply during business hours.</p>
              </InfoCard>
              <InfoCard icon={Mail} title="Email">
                <a href="mailto:quality@durafoaminc.com" className="font-medium text-primary">
                  quality@durafoaminc.com
                </a>
              </InfoCard>
              <InfoCard icon={MapPin} title="Location & hours">
                <div>
                  <h4 className="text-sm font-semibold text-primary">Office</h4>
                  <address className="not-italic text-foreground/85">
                    Durafoam Roofing LLC<br />
                    2810 E Mohawk Ln<br />
                    Phoenix, AZ 85050
                  </address>
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <h4 className="text-sm font-semibold text-primary">Service area</h4>
                  <p className="text-foreground/85">
                    Phoenix, Scottsdale, Mesa, Tempe, Chandler, Glendale, Peoria, and the rest of
                    the Phoenix Valley.
                  </p>
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <h4 className="text-sm font-semibold text-primary">Hours</h4>
                  <p className="text-foreground/85">Mon–Fri 8:00 AM – 5:00 PM</p>
                  <p className="text-foreground/85">Sat by appointment</p>
                  <p className="text-sm text-[var(--muted-foreground)]">Emergency leaks: 24/7 via text</p>
                </div>
              </InfoCard>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-white px-4 py-3 text-[15px] text-foreground placeholder:text-[var(--muted-foreground)] focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30";

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function InfoCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Phone;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-[var(--surface)] text-accent">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <h3 className="text-base font-semibold text-primary">{title}</h3>
      </div>
      <div className="mt-3 space-y-1">{children}</div>
    </div>
  );
}
